import React, { createContext, useContext, useState, useCallback, useMemo, useEffect, useRef } from 'react';

const LoadingContext = createContext();

export const useLoading = () => useContext(LoadingContext);

export const LoadingProvider = ({ children }) => {
    const [loadingSources, setLoadingSources] = useState({}); // Tracks the loading status of various sources
    const [initialLoading, setInitialLoading] = useState(false); // To track Firebase auth initialization from AuthContext
    const isMounted = useRef(true); // To track component mount state

    // Cleanup for when the component unmounts
    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false; // Mark as unmounted when the component unmounts
        };
    }, []);

    // Function to manage loading states, ensuring no unnecessary state updates
    const setLoading = useCallback((isLoading, source) => {
        if (!isMounted.current) {
            console.warn(`[LoadingContext] setLoading called after unmount for source: ${source}, skipping.`);
            return;
        }

//        console.log(`[LoadingContext] setLoading called for source: ${source}, isLoading: ${isLoading}`);

        setLoadingSources((prevSources) => {
            const currentState = prevSources[source];
            const updatedSources = { ...prevSources };

            // Prevent unnecessary updates if the state is already the same
            if (isLoading && currentState === true) {
//                console.log(`[LoadingContext] Source: ${source} is already loading, skipping update.`);
                return prevSources;
            }
            if (!isLoading && currentState === undefined) {
//                console.log(`[LoadingContext] Source: ${source} is not loading, skipping update.`);
                return prevSources;
            }

            // Add or remove the source from loading tracking
            if (isLoading) {
                updatedSources[source] = true; // Add source as loading
//                console.log(`[LoadingContext] Adding new loading source: ${source}`);
            } else {
                delete updatedSources[source]; // Remove source when no longer loading
//                console.log(`[LoadingContext] Removing source: ${source} from tracked loading sources`);
            }

            // Update only if there is an actual change
            if (JSON.stringify(prevSources) !== JSON.stringify(updatedSources)) {
//                console.log(`[LoadingContext] Updated loading sources for source: ${source}, isLoading: ${isLoading}`, updatedSources);
                return updatedSources;
            }

            return prevSources;
        });
    }, []);

    // Calculate the current loading state based on sources
    const isLoading = useMemo(() => {
        const result = initialLoading || Object.keys(loadingSources).length > 0;
//  console.table({ initialLoading, ...loadingSources });

        if (isMounted.current) {
//            console.log('[LoadingContext] Calculated isLoading state:', result, { loadingSources, initialLoading });
        }
        return result;
    }, [loadingSources, initialLoading]);

    // Context value for global access
    const contextValue = useMemo(() => {
        if (isMounted.current) {
//            console.log('[LoadingContext]: Creating context value with isLoading:', isLoading);
        }
        return {
            isLoading,
            setLoading,
            setInitialLoading,  // Exposing setInitialLoading for external use if needed
        };
    }, [isLoading, setLoading]);

    return (
        <LoadingContext.Provider value={contextValue}>
            {children}
        </LoadingContext.Provider>
    );
};

export default LoadingProvider;
